import { useCallback, useEffect, useMemo, useState, type ChangeEvent, type KeyboardEvent } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { IconEye as Eye, IconEyeOff as EyeClosed } from '@tabler/icons-react';
import { Button, Input as HeroUiInput, InputGroup, TextField, toast } from '@heroui/react';
import { trpc } from '@/lib/trpc';
import { useAppDispatch, useAppSelector } from '@/stores/hooks';
import { setCredentials } from '@hs/shared';
import { handleTRPCError } from '@/utils/error-handler';
import wechatLogoUrl from '@/assets/icons/wechat-logo.svg';
import { detectAccountKind, validateAccountInput } from './utils';
import { useWeChatQrLogin } from './use-wechat-qr-login';
import { WX_LOGIN_CONTAINER_CLASS } from './wx-login-style';

function Input(props: Record<string, unknown>) {
  return <HeroUiInput {...(props as object)} />;
}
function InputGroupFieldInput(props: Record<string, unknown>) {
  return <InputGroup.Input {...(props as object)} />;
}

type LoginStep = 'identify' | 'credential';
type CredentialMode = 'password' | 'sms';
type ViewMode = 'account' | 'wechat';

const SMS_COOLDOWN_SECONDS = 60;

export default function LoginPage() {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [viewMode, setViewMode] = useState<ViewMode>('account');
  const [step, setStep] = useState<LoginStep>('identify');
  const [credentialMode, setCredentialMode] = useState<CredentialMode>('password');

  const [account, setAccount] = useState('');
  const [accountError, setAccountError] = useState<string | null>(null);
  const [password, setPassword] = useState('');
  const [smsCode, setSmsCode] = useState('');
  const [isPasswordVisible, setIsPasswordVisible] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [smsCooldown, setSmsCooldown] = useState(0);

  const accountKind = useMemo(() => detectAccountKind(account), [account]);
  const isMobileAccount = accountKind === 'mobile';
  const returnUrl = searchParams.get('returnUrl');
  const authUser = useAppSelector((state) => state.auth.user);

  /** 微信登录成功后的回跳：优先 URL 参数，否则主站首页根路径 */
  const wechatReturnUrl = useMemo(() => {
    if (returnUrl?.trim()) return returnUrl.trim();
    if (typeof window === 'undefined') return null;
    return `${window.location.origin}/`;
  }, [returnUrl]);

  const { bootstrap: wechatBootstrap, loadError: wechatLoadError } = useWeChatQrLogin(
    viewMode === 'wechat',
    wechatReturnUrl
  );

  const handleAuthSuccess = useCallback(
    (data: {
      accessToken: string;
      refreshToken?: string;
      user: Record<string, unknown>;
    }) => {
      dispatch(
        setCredentials({
          user: {
            id: data.user.id as string,
            username: data.user.username as string,
            nickname: (data.user.nickname as string | null) ?? undefined,
            email: (data.user.email as string | null) ?? undefined,
            mobile: (data.user.mobile as string | null) ?? undefined,
            avatarUrl: (data.user.avatarUrl as string | null) ?? undefined,
            status: (data.user.status as string | null) ?? undefined,
            type: (data.user as { type?: string }).type ?? undefined,
            tenantId:
              typeof data.user.tenantId === 'string' ? data.user.tenantId : undefined,
            roles: (data.user as { roles?: unknown }).roles as never,
            apps: (data.user as { apps?: unknown }).apps as never,
          },
          token: data.accessToken,
          refreshToken: data.refreshToken,
        })
      );

      const displayName =
        (data.user.nickname as string | undefined) ||
        (data.user.username as string | undefined) ||
        '用户';
      toast.success('登录成功', { description: `欢迎回来，${displayName}！` });

      const returnUrl = new URLSearchParams(window.location.search).get('returnUrl');
      const targetUrl = returnUrl || '/';
      const isCrossOrigin =
        targetUrl.startsWith('http://') || targetUrl.startsWith('https://');

      if (isCrossOrigin) {
        window.location.href = targetUrl;
      } else {
        navigate(targetUrl, { replace: true });
      }
    },
    [dispatch, navigate]
  );

  const loginMutation = (trpc as any).auth.login.useMutation({
    onSuccess: handleAuthSuccess,
    onError: (error: unknown) => {
      handleTRPCError(error);
      setIsLoading(false);
    },
  });

  const loginByMobileMutation = (trpc as any).auth.loginByMobile.useMutation({
    onSuccess: handleAuthSuccess,
    onError: (error: unknown) => {
      handleTRPCError(error);
      setIsLoading(false);
    },
  });

  const sendSmsCodeMutation = (trpc as any).auth.sendSmsCode.useMutation({
    onSuccess: (data: { expiresIn: number; message?: string }) => {
      setSmsCooldown(SMS_COOLDOWN_SECONDS);
      toast.success('验证码已发送', {
        description: data.message || `验证码 ${Math.floor(data.expiresIn / 60)} 分钟内有效`,
      });
    },
    onError: (error: unknown) => {
      handleTRPCError(error);
    },
  });

  useEffect(() => {
    if (smsCooldown <= 0) return;
    const timer = window.setTimeout(() => setSmsCooldown((s) => s - 1), 1000);
    return () => window.clearTimeout(timer);
  }, [smsCooldown]);

  /** 微信 OAuth 回跳失败：直接展示后端 ErrorResponse.message（URL query） */
  useEffect(() => {
    const message = searchParams.get('message');
    if (!message) return;

    toast.danger('登录失败', { description: message });

    if (searchParams.get('recover') === 'account') {
      setViewMode('account');
      setStep('identify');
      setAccount('');
      setPassword('');
      setSmsCode('');
      setAccountError(null);
    }

    setSearchParams({}, { replace: true });
  }, [searchParams, setSearchParams]);

  /** Cookie 登录成功后（含微信 OAuth 回跳）自动进入系统 */
  useEffect(() => {
    if (!authUser) return;

    const targetUrl = returnUrl || '/';
    const isCrossOrigin =
      targetUrl.startsWith('http://') || targetUrl.startsWith('https://');

    if (isCrossOrigin) {
      window.location.href = targetUrl;
    } else {
      navigate(targetUrl, { replace: true });
    }
  }, [authUser, returnUrl, navigate]);

  const handleContinueIdentify = () => {
    const error = validateAccountInput(account);
    if (error) {
      setAccountError(error);
      return;
    }
    setAccountError(null);
    setStep('credential');
    if (accountKind !== 'mobile') {
      setCredentialMode('password');
    }
  };

  const handleSendSmsCode = () => {
    if (!isMobileAccount) return;
    if (smsCooldown > 0 || sendSmsCodeMutation.isPending) return;
    sendSmsCodeMutation.mutate({
      mobile: account.trim(),
      scene: 'LOGIN',
    });
  };

  const handleSubmitCredential = async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmedAccount = account.trim();

    if (credentialMode === 'password') {
      if (!password) {
        toast.danger('请填写完整信息', { description: '密码不能为空' });
        return;
      }
      setIsLoading(true);
      loginMutation.mutate({
        username: trimmedAccount,
        password,
      });
      return;
    }

    if (!/^\d{6}$/.test(smsCode)) {
      toast.danger('验证码格式不正确', { description: '请输入 6 位数字验证码' });
      return;
    }
    setIsLoading(true);
    loginByMobileMutation.mutate({
      mobile: trimmedAccount,
      code: smsCode,
    });
  };

  const switchToSmsLogin = () => {
    if (!isMobileAccount) return;
    setCredentialMode('sms');
    setPassword('');
    setSmsCode('');
  };

  const switchToPasswordLogin = () => {
    setCredentialMode('password');
    setSmsCode('');
  };

  const title = '欢迎回来';

  const isSubmitting =
    isLoading || loginMutation.isPending || loginByMobileMutation.isPending;

  const smsSendDisabled =
    smsCooldown > 0 || sendSmsCodeMutation.isPending || isSubmitting;

  const smsSendLabel = sendSmsCodeMutation.isPending
    ? '发送中…'
    : smsCooldown > 0
      ? `${smsCooldown}s`
      : '获取验证码';

  return (
    <div className="flex min-h-screen items-center justify-center bg-white p-4">
      <div className="w-full max-w-sm">
        <div className="mb-8 text-center">
          <h1 className="text-2xl font-bold text-foreground">{title}</h1>
          <p className="mt-3 text-sm text-default-600">
            没有账号，
            <Link to="/register" className="font-medium text-accent underline-offset-2 hover:underline">
              立即注册
            </Link>
          </p>
        </div>

        {viewMode === 'wechat' ? (
          <div className="flex flex-col items-center gap-4">
            {wechatLoadError && (
              <p className="text-sm text-danger" role="alert">
                {wechatLoadError}
              </p>
            )}
            {!wechatBootstrap && !wechatLoadError && (
              <p className="text-sm text-default-500">加载微信登录…</p>
            )}
            {wechatBootstrap && !wechatBootstrap.configured && (
              <p className="text-center text-sm text-warning">{wechatBootstrap.message}</p>
            )}
            {wechatBootstrap?.configured && (
              <div id="wx_login_container" className={WX_LOGIN_CONTAINER_CLASS} />
            )}
            <Button
              type="button"
              variant="ghost"
              className="text-primary"
              onPress={() => {
                setViewMode('account');
                setStep('identify');
              }}
            >
              使用注册账号登录
            </Button>
          </div>
        ) : step === 'identify' ? (
          <div className="flex flex-col gap-6">
            <TextField className="w-full flex flex-col gap-2" isInvalid={!!accountError}>
              <label htmlFor="account" className="text-sm font-medium text-foreground">
                账号
              </label>
              <Input
                id="account"
                name="account"
                type="text"
                placeholder="用户名、手机号或邮箱"
                autoComplete="username"
                value={account}
                isDisabled={isSubmitting}
                onChange={(e: ChangeEvent<HTMLInputElement>) => {
                  setAccount(e.target.value);
                  if (accountError) setAccountError(null);
                }}
                onKeyDown={(e: KeyboardEvent<HTMLInputElement>) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleContinueIdentify();
                  }
                }}
              />
              {accountError ? (
                <p className="text-sm text-danger">{accountError}</p>
              ) : null}
            </TextField>

            <Button
              type="button"
              variant="primary"
              className="w-full"
              size="lg"
              onPress={handleContinueIdentify}
            >
              继续
            </Button>
          </div>
        ) : (
          <form className="flex flex-col gap-6" onSubmit={handleSubmitCredential}>
            <TextField className="w-full flex flex-col gap-2">
              <label htmlFor="account-readonly" className="text-sm font-medium text-foreground">
                账号
              </label>
              <Input
                id="account-readonly"
                type="text"
                value={account}
                readOnly
                className="bg-default-50"
              />
            </TextField>

            {credentialMode === 'password' ? (
              <TextField className="w-full flex flex-col gap-2">
                <label htmlFor="password" className="text-sm font-medium text-foreground">
                  密码
                </label>
                <InputGroup aria-label="密码" className="w-full">
                  <InputGroupFieldInput
                    id="password"
                    name="password"
                    type={isPasswordVisible ? 'text' : 'password'}
                    placeholder="输入密码"
                    autoComplete="current-password"
                    value={password}
                    isDisabled={isSubmitting}
                    onChange={(e: ChangeEvent<HTMLInputElement>) => setPassword(e.target.value)}
                  />
                  <InputGroup.Suffix className="px-1">
                    <button
                      type="button"
                      onClick={() => setIsPasswordVisible(!isPasswordVisible)}
                      className="text-default-500 hover:text-default-700"
                      aria-label="切换密码可见性"
                    >
                      {isPasswordVisible ? (
                        <EyeClosed className="h-4 w-4" stroke={1.5} />
                      ) : (
                        <Eye className="h-4 w-4" stroke={1.5} />
                      )}
                    </button>
                  </InputGroup.Suffix>
                </InputGroup>
              </TextField>
            ) : (
              <div className="flex flex-col gap-2">
                <label htmlFor="sms-code" className="text-sm font-medium text-foreground">
                  验证码
                </label>
                <InputGroup aria-label="短信验证码" className="w-full">
                  <InputGroupFieldInput
                    id="sms-code"
                    name="smsCode"
                    type="text"
                    inputMode="numeric"
                    maxLength={6}
                    placeholder="输入验证码"
                    value={smsCode}
                    isDisabled={isSubmitting}
                    onChange={(e: ChangeEvent<HTMLInputElement>) => setSmsCode(e.target.value)}
                    autoComplete="one-time-code"
                  />
                  <InputGroup.Suffix className="px-1">
                    <button
                      type="button"
                      className="shrink-0 whitespace-nowrap px-2 text-sm font-medium text-primary disabled:cursor-not-allowed disabled:text-default-400"
                      disabled={smsSendDisabled}
                      onClick={handleSendSmsCode}
                    >
                      {smsSendLabel}
                    </button>
                  </InputGroup.Suffix>
                </InputGroup>
              </div>
            )}

            <Button
              type="submit"
              variant="primary"
              className="w-full"
              size="lg"
              isDisabled={isSubmitting}
            >
              {isSubmitting ? '登录中…' : '进入'}
            </Button>

            {isMobileAccount && credentialMode === 'password' ? (
              <Button
                type="button"
                variant="ghost"
                className="w-full text-primary"
                onPress={switchToSmsLogin}
              >
                换成验证码登录
              </Button>
            ) : null}

            {isMobileAccount && credentialMode === 'sms' ? (
              <Button
                type="button"
                variant="ghost"
                className="w-full text-primary"
                onPress={switchToPasswordLogin}
              >
                换成密码登录
              </Button>
            ) : null}
          </form>
        )}

        {viewMode === 'account' && step === 'identify' ? (
          <div className="mt-12 flex flex-col items-center gap-4">
            <p className="text-sm text-default-600">使用第三方账号登录</p>
            <button
              type="button"
              aria-label="微信登录"
              className="flex size-12 items-center justify-center rounded-full border border-default-200 bg-white shadow-sm transition hover:bg-default-50"
              onClick={() => setViewMode('wechat')}
            >
              <img src={wechatLogoUrl} alt="" className="size-7" />
            </button>
          </div>
        ) : null}
      </div>
    </div>
  );
}
